/**
 * Dashboard - Farmer Dashboard
 * Business logic untouched — only UI/layout/styling changed.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { GoogleMap, useJsApiLoader, Polygon } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../utils/googleMaps';
import {
  Thermometer, Droplets, Wind, CloudRain, MapPin, Sprout,
  AlertTriangle, ChevronRight, Leaf, Clock, Users
} from 'lucide-react';
import { toast } from 'sonner';
import CropHealthScout from '../components/CropHealthScout';
import Forecast48hModal from '../components/Forecast48hModal';
import { GeoAlertsPanel, VisitRequestButton, ActiveCasesPanel } from '../components/AgronomistFarmerComponents';
import AlertDetailModal from '../components/AlertDetailModal';
import {
  getCachedFields, cacheFields,
  getCachedWeather, cacheWeather,
  getCachedEngineAlerts, cacheEngineAlerts
} from '../utils/cache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [farm, setFarm] = useState(null);
  const [fields, setFields] = useState([]);
  const [weather, setWeather] = useState(null);
  const [engineAlerts, setEngineAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [alertTab, setAlertTab] = useState('engine');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [forecastOpen, setForecastOpen] = useState(false);
  const [showAskAgronomistModal, setShowAskAgronomistModal] = useState(false);
  const [selectedFieldForCase, setSelectedFieldForCase] = useState(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

  const loadData = useCallback(async () => {
    const farmId = user?.farm_id;
    
    // Check cache first for quick initial render
    const cachedFields = farmId ? getCachedFields(farmId) : null;
    const cachedWeather = farmId ? getCachedWeather(farmId) : null;
    
    if (cachedFields?.data && !cachedFields.isExpired) {
      setFields(cachedFields.data);
    }
    if (cachedWeather?.data && !cachedWeather.isExpired) {
      setWeather(cachedWeather.data);
    }

    try {
      const [farmRes, fieldsRes, weatherRes] = await Promise.all([
        axios.get(`${API}/farm`, { headers: getAuthHeaders() }),
        axios.get(`${API}/fields`, { headers: getAuthHeaders() }),
        axios.get(`${API}/weather/current`, { headers: getAuthHeaders() })
      ]);
      setFarm(farmRes.data);
      setFields(fieldsRes.data);
      setWeather(weatherRes.data);
      
      // Cache the fresh data
      if (farmId) {
        cacheFields(farmId, fieldsRes.data);
        cacheWeather(farmId, weatherRes.data);
      }
      
      const allAlerts = [];
      for (const field of fieldsRes.data.slice(0, 5)) {
        // Check alert cache
        const cachedAlerts = getCachedEngineAlerts(field.id);
        if (cachedAlerts?.data && !cachedAlerts.isExpired) {
          allAlerts.push(...cachedAlerts.data.map(a => ({ ...a, fieldName: field.name, field_id: field.id })));
          continue;
        }
        
        try {
          // Use the with-advice endpoint to get AI advice attached
          const alertsRes = await axios.get(`${API}/fields/${field.id}/alerts/with-advice`, { headers: getAuthHeaders() });
          const fieldAlerts = alertsRes.data.map(a => ({ ...a, fieldName: field.name, field_id: field.id }));
          allAlerts.push(...fieldAlerts);
          // Cache alerts
          cacheEngineAlerts(field.id, alertsRes.data);
        } catch (e) {
          // Fallback to regular alerts endpoint
          try {
            const alertsRes = await axios.get(`${API}/fields/${field.id}/alerts`, { headers: getAuthHeaders() });
            const fieldAlerts = alertsRes.data.map(a => ({ ...a, fieldName: field.name, field_id: field.id }));
            allAlerts.push(...fieldAlerts);
            cacheEngineAlerts(field.id, alertsRes.data);
          } catch (e2) {}
        }
      }
      setEngineAlerts(allAlerts);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally { setLoading(false); }
  }, [getAuthHeaders, user?.farm_id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDismissAlert = async (alert) => {
    try {
      await axios.post(`${API}/fields/${alert.field_id}/alerts/${alert.id}/dismiss`, {}, { headers: getAuthHeaders() });
      setEngineAlerts(prev => prev.filter(a => a.id !== alert.id));
      setSelectedAlert(null);
      toast.success('Alert dismissed');
    } catch (error) { toast.error('Failed to dismiss alert'); }
  };

  const totalArea = fields.reduce((sum, f) => sum + (f.areaHa || f.area_hectares || 0), 0);
  const activeFields = fields.filter(f => !f.is_harvested);

  if (loading) {
    return <Layout><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 256 }}><div className="spinner" /></div></Layout>;
  }

  return (
    <Layout onOpenChat={() => setChatOpen(true)}>
      <div data-testid="dashboard-page" style={{ maxWidth: 1280 }}>
        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: 6 }}>Dashboard</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
              <MapPin size={14} />
              <span>{farm?.name || 'My Farm'}</span>
              <span style={{ opacity: 0.5 }}>({farm?.latitude?.toFixed(4)}°N, {farm?.longitude?.toFixed(4)}°E)</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div className="card-floating stat-chip" data-testid="stat-active-fields">
              <span className="stat-chip-label">Active Fields</span>
              <span className="stat-chip-value">{activeFields.length}</span>
            </div>
            <div className="card-floating stat-chip" data-testid="stat-farm-size">
              <span className="stat-chip-label">Farm Size</span>
              <span className="stat-chip-value">{totalArea.toFixed(1)} ha</span>
            </div>
            <button
              onClick={() => setShowAskAgronomistModal(true)}
              className="btn-depth-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              data-testid="dashboard-ask-agronomist-btn"
            >
              <Users size={16} />
              Ask Agronomist
            </button>
          </div>
        </div>

        {/* ── Weather ── */}
        <section style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <CloudRain size={18} style={{ color: 'var(--gold)' }} />
            <h2 style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>Weather Overview</h2>
            <button 
              onClick={() => setForecastOpen(true)}
              data-testid="view-48h-forecast-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: 'linear-gradient(135deg, rgba(198,170,88,0.2), rgba(198,170,88,0.1))',
                border: '1px solid rgba(198,170,88,0.4)',
                borderRadius: 10,
                color: 'var(--gold)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(198,170,88,0.15)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(198,170,88,0.3), rgba(198,170,88,0.2))';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(198,170,88,0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(198,170,88,0.2), rgba(198,170,88,0.1))';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(198,170,88,0.15)';
              }}
            >
              <Clock size={14} />
              48H Forecast
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
            <WeatherCard icon={<Thermometer size={18} />} label="Temperature" value={weather?.temperature?.toFixed(1) || '--'} unit="°C" />
            <WeatherCard icon={<Droplets size={18} />} label="Humidity" value={weather?.humidity?.toFixed(0) || '--'} unit="%" />
            <WeatherCard icon={<Wind size={18} />} label="Wind Speed" value={((weather?.windSpeed || 0) * 3.6).toFixed(1)} unit="km/h" />
            <WeatherCard icon={<CloudRain size={18} />} label="Precipitation" value={weather?.rain_mm_hr?.toFixed(1) || '0.0'} unit="mm/h"
              subtitle={`Next 6h: ${weather?.rain_next_6h_mm?.toFixed(1) || 0}mm | 24h: ${weather?.rain_next_24h_mm?.toFixed(1) || 0}mm`} />
          </div>
        </section>

        {/* ── Active Cases ── */}
        {farm?.id && <ActiveCasesPanel farmId={farm.id} />}

        {/* ── Field Alerts ── */}
        <section style={{ marginBottom: 28 }}>
          <section className="card-floating" style={{ position: 'relative' }} data-testid="alerts-panel">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={18} style={{ color: 'var(--warning)' }} />
                <span style={{ fontWeight: 600 }}>Field Alerts</span>
                {engineAlerts.length > 0 && (
                  <span style={{ background: 'var(--danger)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 8 }}>{engineAlerts.length}</span>
                )}
              </div>
            </div>
            <div className="card-body">
              {/* Tabs */}
              <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-soft)', marginBottom: 14 }}>
                {['engine', 'geo'].map(t => (
                  <button key={t} className={`tab-btn${alertTab === t ? ' active' : ''}`}
                    onClick={() => setAlertTab(t)} data-testid={`alert-tab-${t}`}
                    style={{ padding: '10px 16px', fontSize: 13 }}>
                    {t === 'engine' ? 'Alerts' : 'Agronomist Alerts'}
                  </button>
                ))}
              </div>
              <div style={{ minHeight: 100 }}>
                {alertTab === 'engine' ? (
                  engineAlerts.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 28, gap: 10 }}>
                      <Leaf size={40} style={{ color: 'var(--text-muted)' }} />
                      <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No active alerts</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                      {engineAlerts.map((alert, idx) => (
                        <AlertItem key={alert.id || idx} alert={alert} onClick={() => setSelectedAlert(alert)} />
                      ))}
                    </div>
                  )
                ) : (
                  farm?.id && <GeoAlertsPanel farmId={farm.id} />
                )}
              </div>
            </div>
          </section>
        </section>

        {/* ── Visit Request ── */}
        <VisitRequestButton />

        {/* ── Active Fields ── */}
        <section style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Sprout size={18} style={{ color: 'var(--gold)' }} />
            <h2 style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>Active Fields</h2>
            <button className="btn-depth-secondary" onClick={() => navigate('/fields')} data-testid="manage-fields-btn" style={{ padding: '8px 14px', fontSize: 13 }}>Manage</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {activeFields.slice(0, 4).map(field => (
              <FieldCard key={field.id} field={field} isLoaded={isLoaded} onClick={() => navigate(`/fields/${field.id}`)} />
            ))}
            {activeFields.length === 0 && (
              <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 48, gap: 14, border: '1px dashed var(--border)', borderRadius: 18 }}>
                <Sprout size={40} style={{ color: 'var(--text-muted)' }} />
                <p style={{ color: 'var(--text-muted)' }}>No active fields</p>
                <button className="btn-depth-primary" onClick={() => navigate('/fields')}>Add Field</button>
              </div>
            )}
          </div>
        </section>
      </div>

      {chatOpen && <CropHealthScout isOpen={chatOpen} onClose={() => setChatOpen(false)} />}
      {forecastOpen && <Forecast48hModal isOpen={forecastOpen} onClose={() => setForecastOpen(false)} />}
      {selectedAlert && (
        <AlertDetailModal alert={selectedAlert} isOpen={true}
          onClose={() => setSelectedAlert(null)}
          onDismiss={() => handleDismissAlert(selectedAlert)}
          onOpenField={() => { setSelectedAlert(null); navigate(`/fields/${selectedAlert.field_id}`); }} />
      )}
      
      {/* Ask Agronomist Field Selector Modal */}
      {showAskAgronomistModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
          <div className="card-floating max-w-md w-full p-6 anim-scale">
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: 'var(--text)' }}>Select Field for Agronomist Case</h3>
            <p style={{ fontSize: 13, marginBottom: 16, color: 'var(--text-muted)' }}>
              Choose which field you want to ask about:
            </p>
            
            {fields.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>
                <Sprout size={40} style={{ marginBottom: 12, opacity: 0.5 }} />
                <p>No fields available</p>
                <button onClick={() => { setShowAskAgronomistModal(false); navigate('/fields'); }} className="btn-depth-primary mt-4">
                  Add Field First
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
                {fields.map(f => (
                  <button
                    key={f.id}
                    onClick={() => {
                      setShowAskAgronomistModal(false);
                      navigate(`/fields/${f.id}?openCase=true`);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 14,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid var(--border-soft)',
                      borderRadius: 12,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      color: 'var(--text)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(198,170,88,0.1)';
                      e.currentTarget.style.borderColor = 'var(--gold)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                      e.currentTarget.style.borderColor = 'var(--border-soft)';
                    }}
                    data-testid={`select-field-${f.id}`}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{f.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{f.crop} • {f.areaHa?.toFixed(1) || 0} ha</div>
                    </div>
                    <ChevronRight size={18} style={{ color: 'var(--text-muted)' }} />
                  </button>
                ))}
              </div>
            )}
            
            <button
              onClick={() => setShowAskAgronomistModal(false)}
              className="btn-depth-secondary w-full mt-4"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

/* ── WeatherCard ── */
const WeatherCard = ({ icon, label, value, unit, subtitle }) => (
  <div className="card-floating" style={{ position: 'relative', padding: 18 }} data-testid={`weather-${label.toLowerCase().replace(' ', '-')}`}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ color: 'var(--gold)' }}>{icon}</span>
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</span>
    </div>
    <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1 }}>
      {value}<span style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 3 }}>{unit}</span>
    </div>
    {subtitle && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{subtitle}</div>}
  </div>
);

/* ── AlertItem ── */
const AlertItem = ({ alert, onClick }) => (
  <button onClick={onClick} data-testid={`alert-item-${alert.id}`}
    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-soft)', borderRadius: 12, width: '100%', textAlign: 'left', cursor: 'pointer', transition: 'background .12s', color: 'var(--text)' }}>
    <div style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,0.15)', borderRadius: 7, color: 'var(--warning)', flexShrink: 0 }}>
      <AlertTriangle size={14} />
    </div>
    <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{alert.title || alert.alert_type}</span>
      {alert.fieldName && <span className="badge-depth badge-neutral" style={{ fontSize: 10 }}>{alert.fieldName}</span>}
      {alert.severity && (
        <span className={`badge-depth ${alert.severity?.toLowerCase() === 'high' ? 'badge-danger' : alert.severity?.toLowerCase() === 'medium' ? 'badge-warning' : 'badge-info'}`} style={{ fontSize: 10 }}>
          {alert.severity}
        </span>
      )}
      {alert.ai_advice && (
        <span style={{ 
          display: 'inline-flex', alignItems: 'center', gap: 3,
          background: 'rgba(198,170,88,0.15)', color: 'var(--gold)', 
          fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 6
        }} data-testid="ai-badge">
          AI
        </span>
      )}
      <p style={{ width: '100%', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{alert.message || alert.description}</p>
    </div>
    <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 4 }} />
  </button>
);

/* ── FieldCard ── */
const FieldCard = ({ field, isLoaded, onClick }) => {
  const metrics = field.metrics_cache || {};
  return (
    <button onClick={onClick} className="card-floating" data-testid={`field-card-${field.id}`}
      style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer', textAlign: 'left', width: '100%', padding: 0, border: '1px solid var(--border-soft)', borderRadius: 18 }}>
      <div style={{ height: 140, background: 'rgba(0,0,0,0.25)' }}>
        {isLoaded && field.geometry?.coordinates?.[0] ? (
          <FieldMiniMap geometry={field.geometry} isLoaded={isLoaded} />
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}><MapPin size={28} /></div>
        )}
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{field.name}</span>
          <span className="badge-depth badge-gold" style={{ fontSize: 11 }}>{field.crop || 'Unknown'}</span>
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span>{metrics.das || 0} DAS</span>
          <span>{metrics.gdd_total?.toFixed(0) || 0} GDD</span>
          <span>{metrics.rain_7d_mm?.toFixed(1) || 0} mm 7d</span>
        </div>
      </div>
    </button>
  );
};

/* ── FieldMiniMap ── */
const FieldMiniMap = ({ geometry, isLoaded }) => {
  if (!isLoaded || !geometry?.coordinates?.[0]) return null;
  const polygonCoords = geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
  const center = polygonCoords.reduce((acc, c) => ({ lat: acc.lat + c.lat / polygonCoords.length, lng: acc.lng + c.lng / polygonCoords.length }), { lat: 0, lng: 0 });
  return (
    <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} center={center} zoom={15}
      options={{ mapTypeId: 'satellite', disableDefaultUI: true, gestureHandling: 'none', clickableIcons: false }}>
      <Polygon paths={polygonCoords} options={{ fillColor: '#C6AA58', fillOpacity: 0.35, strokeColor: '#C6AA58', strokeOpacity: 1, strokeWeight: 2 }} />
    </GoogleMap>
  );
};

export default Dashboard;
